// ============================================================
// HOOKED — ANGLER STATE
// Local-first (Firebase joins later — its lane is reserved).
//
// THE DIAL LAW: internal keys are Empire vocabulary, always.
// Screen labels localize (WOVEN):  anima→GRIT · khao→FLOW ·
// lyt→HOOK · lyf→LINE · renown→REPUTE · scales→SCALES.
//
// LAWS ENFORCED HERE:
//   ANIMA DOCTRINE  — Grit starts 30, caps 50, and is SPENDABLE
//                     (True Strike · Surge · Slack).
//   KHAO GROWTH LAW — land a fish having spent zero Flow that
//                     cast → maxKhao permanently +1. Restraint
//                     compounds.
//   LYF CYCLE       — Line resets EVERY cast. Chip tension is
//                     worthless; a fish breaks you in one fight
//                     or not at all.
//   NECROPOLIS      — the Murk is a RESOURCE, not a trash can.
//                     An escaped fish is Marked: it remembers
//                     you, and you it — wider perfect windows
//                     against its whole species next time.
//   CURRENCY LANE   — SCALES is HOOKED's own currency, delta-
//                     transacted in its own lane (GAIN_SCALES /
//                     BUY_ITEM only). No other action touches it.
//   PROVENANCE LAW  — unlocked is APPEND-ONLY; every entry
//                     carries a SourceSigil (STARTER / BOUGHT /
//                     EARNED) and a namespaced source. Earned
//                     glows brighter than bought.
//   DERIVE, DON'T STORE — tackle/wardrobe store equipped IDS;
//                     effective HOOK/LINE derive via
//                     tackleStats() at read time.
//   DELTA WRITES    — every currency/resource change is a delta,
//                     never a full-object overwrite. This shape
//                     ports 1:1 to Firebase transactions later.
//   GRANT-BEFORE-FLAG — catches are granted before any
//                     completion flag is set.
// ============================================================

import { STARTER_KIT, PRICE_TEST_MODE } from "./tackle";

export const ANIMA_START   = 30;   // ✦ the numbers travel
export const MAX_ANIMA_NAT = 50;   // ✦
export const KHAO_START    = 3;
export const LYT_BASE      = 10;   // hook strength — progress per clean beat
export const LYF_BASE      = 60;   // line strength — tension capacity per cast

// ── Anima spends (three, per the Doctrine) ───────────────────
export const SPENDS = {
  TRUE_STRIKE: { cost: 8, label: "TRUE STRIKE", desc: "This strike's perfect window doubles." },
  SURGE:       { cost: 5, label: "SURGE",       desc: "Your next two beats land perfect, unbidden." },
  SLACK:       { cost: 3, label: "SLACK",       desc: "All tension on the line releases." },
};

// ── Flow spends ──────────────────────────────────────────────
export const FLOW_SPENDS = {
  MOONWATER:  { cost: 2, label: "MOONWATER",  desc: "The strike ring closes 40% slower." },
  SWEETEN:    { cost: 1, label: "SWEETEN",    desc: "Richer waters this cast." },
  EYES_BELOW: { cost: 1, label: "EYES BELOW", desc: "See what took the bait before you strike." },
};

export const initialAngler = {
  anima: ANIMA_START,
  maxAnima: MAX_ANIMA_NAT,
  khao: KHAO_START,          // refills to maxKhao every cast (the beat)
  maxKhao: KHAO_START,
  lyt: LYT_BASE,
  lyf: LYF_BASE,
  renown: 0,
  // TEST MODE (caP'n's 2-for-1, July 20): a stipend so the whole
  // 1-❖ shelf is buyable on a fresh save without grinding catches
  // first — while catches still add MORE on top (currency proven
  // in both directions). Gated on the SAME flag as the prices.
  //   ↻ TO RESTORE: set PRICE_TEST_MODE = false in tackle.js.
  // NOTE: only applies to NEW saves; an existing localStorage
  // angler keeps its balance (merge-over-initial). Clear the save
  // to see the stipend.
  scales: PRICE_TEST_MODE ? 100 : 0,   // HOOKED's currency — its own delta lane
  catches: [],               // { fishId, grade, weight, at, sigil } — provenance-stamped
  murk: {},                  // { [fishId]: escapeCount } — the Necropolis, as a resource
  curios: [],                // wizard's caps and stranger things
  flowSpentThisCast: false,  // growth-law tracker

  // ── Identity — equipped IDS only (derive, don't store) ─────
  tackle:   { rod: "driftwood_rod", line: "hemp_line", bobber: "cork_bob", lure: "bare_hook" },
  wardrobe: { head: "wharf_cap", face: null, body: "gold_coat", back: null, aura: null, title: "dockhand" },

  // Append-only ownership ledger — SourceSigil on every grant.
  unlocked: STARTER_KIT.map(id => ({ id, at: 0, sigil: "STARTER", source: "hooked:kit" })),
};

export function anglerReducer(state, action) {
  switch (action.type) {

    // ── Cast beat: Khao refills, Lyf resets (the Lyf Cycle) ──
    case "NEW_CAST":
      return { ...state, khao: state.maxKhao, lyf: LYF_BASE, flowSpentThisCast: false };

    case "SPEND_ANIMA": {
      const cost = action.cost ?? 0;
      if (state.anima < cost) return state;
      return { ...state, anima: state.anima - cost };          // delta ✓
    }

    case "SPEND_FLOW": {
      const cost = action.cost ?? 0;
      if (state.khao < cost) return state;
      return { ...state, khao: state.khao - cost, flowSpentThisCast: true };
    }

    case "DIM_ANIMA": {
      const amt = action.amount ?? 0;
      return { ...state, anima: Math.max(0, state.anima - amt) };
    }

    case "RESTORE_ANIMA":
      return { ...state, anima: Math.min(state.maxAnima, state.anima + (action.amount ?? 0)) };

    // ── SCALES lane — deltas only, nothing else touches it ───
    case "GAIN_SCALES":
      return { ...state, scales: state.scales + Math.max(0, action.amount ?? 0) };

    // ── BUY: spend scales (delta) + append to the ledger ─────
    case "BUY_ITEM": {
      const { item } = action;
      if (!item || item.price == null) return state;
      if (state.unlocked.some(u => u.id === item.id)) return state;   // no double-buy
      if (state.scales < item.price) return state;
      return {
        ...state,
        scales: state.scales - item.price,                            // delta ✓
        unlocked: [...state.unlocked, {                               // append-only ✓
          id: item.id, at: Date.now(), sigil: "BOUGHT", source: "hooked:wyllys",
        }],
      };
    }

    // ── EARN: the brighter sigil. Free, condition-checked upstream ─
    case "UNLOCK_ITEM": {
      const { itemId, source = "hooked:earned" } = action;
      if (!itemId || state.unlocked.some(u => u.id === itemId)) return state;
      return {
        ...state,
        unlocked: [...state.unlocked, { id: itemId, at: Date.now(), sigil: "EARNED", source }],
      };
    }

    // ── EQUIP: ids only. null unequips optional slots ────────
    case "EQUIP_ITEM": {
      const { kind, slot, itemId } = action;                          // kind: tackle | wardrobe
      if (kind !== "tackle" && kind !== "wardrobe") return state;
      if (itemId !== null && !state.unlocked.some(u => u.id === itemId)) return state;
      return { ...state, [kind]: { ...state[kind], [slot]: itemId } };
    }

    // ── LAND: grant-before-flag, growth law, provenance ──────
    case "LAND_FISH": {
      const { fish, grade, weight } = action;
      // GRANT first —
      const entry = {
        fishId: fish.id, grade, weight,
        at: Date.now(),
        sigil: "EARNED",                       // SourceSigil — earned glows brighter
      };
      let next = {
        ...state,
        catches: [...state.catches, entry],    // append-only ✓
        renown: state.renown + (fish.renown ?? 0),
      };
      // — THEN the growth law flag check (restraint compounds)
      if (!state.flowSpentThisCast) {
        next = { ...next, maxKhao: state.maxKhao + 1 };
      }
      return next;
    }

    case "GAIN_CURIO":
      return {
        ...state,
        curios: [...state.curios, { id: action.curio.id, at: Date.now(), sigil: "EARNED" }],
        renown: state.renown + (action.curio.renown ?? 0),
      };

    // ── ESCAPE: into the Murk — a resource, not a trash can ──
    case "FISH_ESCAPED": {
      const id = action.fishId;
      return {
        ...state,
        murk: { ...state.murk, [id]: (state.murk[id] ?? 0) + 1 },
      };
    }

    default:
      return state;
  }
}

// ── Murk bonus — the Necropolis pays out ─────────────────────
// Each prior escape of this fish widens your perfect windows
// against it by 12%, capped at +36%. It remembers you too.
export function murkBonus(state, fishId) {
  const n = state.murk?.[fishId] ?? 0;
  return 1 + Math.min(n, 3) * 0.12;
}
